const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/middleware/patapi.ts';
/**
 * PatApi redux middleware
 *
 * this module intercepts actions with a certain object
 * signature and handles network traffic with kisim backend
 */
import _ from 'lodash';
import { Store } from 'redux';
import IntlMessageFormat from 'intl-messageformat';
import { pushLog } from '@cistec/common_web/actions/logActions';
import createErrorMessage from '@cistec/common_web/middleware/kisapi/errors';
import { showSnackbar, showUncaughtErrorDialog } from '@cistec/common_web/actions/uiActions';
import { GenericRequest, IAsyncMiddleware } from '@cistec/common_web/middleware/genericMiddleware';
import { DEFAULT_LOCALE, intlLanguagesMap } from '@cistec/common_web/utils/intlUtils';

// Action key that carries API call info interpreted by this middleware.
// NOTE: We might like to use ES6 Symbols to generate unique key here, but for
// now jasmine testing makes problems (for prototype stay with string)
export const PATAPI = 'PATAPI'; // Symbol('Call PATAPI');

export interface IPatapiRequest extends GenericRequest {
    isNoAuth?: boolean;
    isExternalApi?: boolean;
    callKisimSynedraServer?: boolean;
}

export interface IPatApiConfig {
    /**
     * define base url of api backend e.g. http://whatever.com
     * This may be null, in this case, patapi module 'balancer.ts' should be included in the pipeline
     */
    baseUrl?: string | string[];

    /**
     * define http request config to overwrite built in defaults for all requests
     */
    httpConfig?: RequestInit;
    /**
     * configure an array of transformation function that the http request will be piped through
     */
    pipeline: IAsyncMiddleware[];
}

// parse response according to content-type header
export function parseBody(response: Response): Promise<any> {
    // an error type response doesn't contain any headers, but we want to make sure
    // to return an empty json object
    if (response.type === 'error') {
        return Promise.resolve({});
    }

    const contentType = response.headers.get('content-type')?.split?.(';');
    if (contentType && contentType.includes('application/json')) {
        return response.json();
    } else if (contentType && contentType.includes('application/pdf')) {
        return response.arrayBuffer();
    } else {
        return response.text();
    }
}

/**
 * Redux middleware for handling PATAPI actions
 */
export default function patApiMiddleware(config: IPatApiConfig) {
    const defaultRequest: RequestInit = {
        method: 'GET',
        headers: {
            accept: 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
        },
        mode: 'cors',
        credentials: 'same-origin',
        cache: 'default',
    };

    return (store: Store) => (next: any) => async (action: any) => {
        // assert correct format of action object
        if (typeof action[PATAPI] === 'undefined') {
            return next(action);
        }
        if (_.isEmpty(config.pipeline)) {
            throw new Error('PatApi middleware is not configured correctly.');
        }
        if (!Array.isArray(action.type) || action.type.length !== 3) {
            throw new Error('Provided action type is wrong');
        }

        Object.assign(defaultRequest, config.httpConfig);
        const chain = _.flowRight(config.pipeline.map((item: any) => item(store)))();

        const patApiCall = Object.assign({}, action[PATAPI]);
        if (!patApiCall?.isExternalApi) {
            patApiCall.endpoint =
                (patApiCall.baseUrl || (Array.isArray(config.baseUrl) ? config.baseUrl[0] : config.baseUrl) || '') +
                patApiCall.endpoint;
        }
        patApiCall.requestConfig = Object.assign({}, defaultRequest, patApiCall.requestConfig);
        const [pending, success, failed] = action.type;

        // callout pending action only if the action is not tagged to been replayed

        if (!action.meta || (action.meta && !action.meta.replay)) {
            Promise.resolve().then(() =>
                next({
                    type: pending,
                    meta: action.meta || {},
                })
            );
        }
        const response = await chain(patApiCall);

        // TODO: fix temporary hack to count api timeouts
        if (response.status === 504) {
            store.dispatch(pushLog('warning', `PATAPI status ${response.status}`, { location: 'patapi.ts' }));
        }

        const wasSuccessful = response.status < 400 && response.type !== 'error';
        // error handling and matching of accept headers and transformation function
        // needs to get much more sophisticated
        let content: any;
        try {
            // TODO: the response parser still needs to be used according to accept header settings
            // and handle at least text, json and binary blobs correctly
            content = await parseBody(response);
        } catch (e) {
            // suppress error log if call has failed; in most cases the content is invalid in these cases, spamming the console
            if (wasSuccessful) {
                // probably no valid json in response body
                console.log(e);
            }
            content = {};
        }

        // form regular action that can be consumed by reducer
        // content.error_description might no be available => TODO: include more checks
        // assert that action.meta is either undefined or an object.
        const actionMeta = action.meta || {};
        if (typeof actionMeta !== 'object') {
            throw new Error('meta entry for patapi middleware needs to be an object or undefined');
        }
        const online: boolean = store.getState().network.get('online');

        if (!wasSuccessful && !actionMeta.suppressErrorMsg && response.status !== 401 && online) {
            // transform non-object contents into an object to eliminate crashes caused by sloppy error messages sent by PATAPI
            if (typeof content !== 'object') {
                content = {
                    Errors: [{ Msg: content }],
                };
            }
            // TODO: make error handling more fine grained. API mostly doesn't give user friendly error messages
            // content.error_description = content.error_description || 'Ein Netzwerkfehler ist aufgetreten';

            // if Error array is returned take first message as error description
            // this might not be the ideal solution, but better than nothing for now.
            if (actionMeta.overrideErrorMsg) {
                const locale = store.getState().i18n.get('locale');
                const map = intlLanguagesMap[locale] || intlLanguagesMap[DEFAULT_LOCALE];
                const msg = new IntlMessageFormat(map[actionMeta.overrideErrorMsg.id], locale);
                content.error_description = msg.format();
            } else {
                const arrayVariant = content.Errors && content.Errors[0] && content.Errors[0].Msg;
                const qstructVariant = content.Errors && content.Errors['1'] && content.Errors['1'].Msg;
                content.error_description = arrayVariant || qstructVariant;
            }

            _.isEmpty(content.error_description)
                ? next(showSnackbar(createErrorMessage(response)))
                : next(showSnackbar({ id: 'NET::ERR', defaultMessage: content.error_description }));
        }

        const hasMore = typeof content === 'object' && content?.Info?.HasMoreResultRows === '1';

        if (wasSuccessful && hasMore) {
            // PATAPI sent us back a flag indicating that the returned result set is incomplete,
            // because the number of entries exceeded the MaxResultRows parameter.

            if (actionMeta.showDlgWhenResultHasMoreRows) {
                next(showUncaughtErrorDialog(null, null, null, 'patapiResultHasMoreRows'));
            }
        }

        if (_.isEmpty(content.error_description) && !wasSuccessful) {
            const errMessage = createErrorMessage(response);
            const locale = store.getState().i18n.get('locale');
            const map = intlLanguagesMap[locale] || intlLanguagesMap[DEFAULT_LOCALE];
            const msg = new IntlMessageFormat(map[errMessage.id!!], locale);
            // if error message comes as html from webserver instead of kisserver.dll
            if (response.headers.get('content-type') !== 'application/json') {
                content = { returnedValue: content };
            }
            content.error_description = msg.format();
        }
        const headersMeta = response.headers.get('ETag') ? { etag: response.headers.get('ETag') } : {};
        if (response.status !== 401) {
            Promise.resolve().then(() =>
                next({
                    type: wasSuccessful ? success : failed,
                    payload: content,
                    meta: Object.assign({}, _.omit(actionMeta, 'replay'), headersMeta, {
                        responseStatus: response.status,
                    }),
                })
            );
        }
    };
}
